import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";

const AVATAR_COLORS = [
  "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
];

const MODE_CONFIG = {
  "Bank Deposit":           { icon: "Building2",      color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"        },
  "Cash":                   { icon: "Banknote",       color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"    },
  "Cheque":                 { icon: "FileCheck",      color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" },
  "Credit Card":            { icon: "CreditCard",     color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  "Debit Card":             { icon: "CreditCard",     color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" },
  "Demand Draft":           { icon: "FileText",       color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  "Online Payment Gateway": { icon: "Globe",          color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"        },
  "Online Transfer":        { icon: "ArrowRightLeft", color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300"        },
  "PayPal":                 { icon: "Wallet",         color: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"            },
  "Wire Transfer":          { icon: "Zap",            color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300"        },
};

const ActionBtn = ({ icon, label, color, onClick, disabled }) => (
  <div className="relative group">
    <button onClick={onClick} disabled={disabled}
      className={`p-1.5 rounded-lg transition-colors ${disabled ? "opacity-30 cursor-not-allowed" : color}`}>
      <Icon name={icon} size={14} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

// ── Refunds Table ─────────────────────────────────────────────────────────────
export const PurchaseRefundsTable = ({ refunds, onDelete }) => {
  if (!refunds.length) return null;
  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {["Refund Date", "Vendor", "Bill #", "Refund #", "Mode", "Amount (SR)", "Actions"].map((h) => (
                <th key={h} className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {refunds.map((r, i) => <RefundRow key={r.id} refund={r} index={i} onDelete={onDelete} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RefundRow = ({ refund, index, onDelete }) => {
  const navigate           = useNavigate();
  const [showDel, setShowDel] = useState(false);
  const modeConfig  = MODE_CONFIG[refund.refundMode] || { icon: "CircleDollarSign", color: "bg-gray-100 text-gray-600" };
  const initials    = (refund.vendorName || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {refund.refundDate
            ? new Date(refund.refundDate).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" })
            : "—"}
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}>
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">{refund.vendorName || "—"}</span>
          </div>
        </td>
        <td className="px-5 py-4">
          <span className="font-mono text-sm text-primary font-semibold">{refund.billNumber || "—"}</span>
        </td>
        <td className="px-5 py-4">
          <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded text-foreground">{refund.refundNumber || "—"}</span>
        </td>
        <td className="px-5 py-4">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${modeConfig.color}`}>
            <Icon name={modeConfig.icon} size={11} />
            {refund.refundMode || "—"}
          </span>
        </td>
        <td className="px-5 py-4">
          <span className="font-semibold text-green-600 dark:text-green-400">
            + {parseFloat(refund.netAmount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
          </span>
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            <ActionBtn icon="Receipt"  label="View Receipt" color="text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              onClick={() => toast("View receipt — coming soon")} />
            <ActionBtn icon="Trash2"   label="Delete"       color="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => setShowDel(true)} />
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDel && (
          <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <td colSpan={7} className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete refund <strong>{refund.refundNumber}</strong> from <strong>{refund.vendorName}</strong>? This cannot be undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button onClick={() => setShowDel(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors">
                    Cancel
                  </button>
                  <button onClick={() => { onDelete(refund.id); setShowDel(false); }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

// ── Credit Notes Table ────────────────────────────────────────────────────────
export const PurchaseCreditNotesTable = ({ creditNotes, onDelete }) => {
  if (!creditNotes.length) return null;
  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {["Credit Note Date", "Vendor", "Bill #", "Credit Note #", "Amount (SR)", "VAT (SR)", "Net (SR)", "Actions"].map((h) => (
                <th key={h} className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {creditNotes.map((c, i) => <CreditNoteRow key={c.id} creditNote={c} index={i} onDelete={onDelete} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CreditNoteRow = ({ creditNote, index, onDelete }) => {
  const [showDel, setShowDel] = useState(false);
  const initials    = (creditNote.vendorName || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {creditNote.creditNoteDate
            ? new Date(creditNote.creditNoteDate).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" })
            : "—"}
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}>
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">{creditNote.vendorName || "—"}</span>
          </div>
        </td>
        <td className="px-5 py-4">
          <span className="font-mono text-sm text-primary font-semibold">{creditNote.billNumber || "—"}</span>
        </td>
        <td className="px-5 py-4">
          <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded text-foreground">{creditNote.creditNoteNumber || "—"}</span>
        </td>
        <td className="px-5 py-4">
          <span className="font-semibold text-foreground">
            {parseFloat(creditNote.totalAmount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
          </span>
        </td>
        <td className="px-5 py-4 text-sm text-muted-foreground">
          {parseFloat(creditNote.totalVat || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
        </td>
        <td className="px-5 py-4">
          <span className="font-semibold text-violet-600 dark:text-violet-400">
            {parseFloat(creditNote.netAmount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
          </span>
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            <ActionBtn icon="Eye"    label="View"   color="text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              onClick={() => toast("View credit note — coming soon")} />
            <ActionBtn icon="Trash2" label="Delete" color="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => setShowDel(true)} />
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDel && (
          <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <td colSpan={8} className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete credit note <strong>{creditNote.creditNoteNumber}</strong> from <strong>{creditNote.vendorName}</strong>? This cannot be undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button onClick={() => setShowDel(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors">
                    Cancel
                  </button>
                  <button onClick={() => { onDelete(creditNote.id); setShowDel(false); }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

export default PurchaseRefundsTable;